package com.evoapps.evoshift.filedrop

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.ErrorOutline
import androidx.compose.material.icons.filled.UploadFile
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.SemanticsPropertyKey
import androidx.compose.ui.semantics.SemanticsPropertyReceiver
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.Gold
import com.evoapps.evoshift.ui.GoldTintedWhite
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.asString
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.close
import org.jetbrains.compose.resources.stringResource

/** Test tags of [FileDropArea]. */
object FileDropTags {
    const val AREA = "file_drop_area"
    const val HIGHLIGHT = "file_drop_highlight"
    const val MESSAGE = "file_drop_message"

    /** The upload places (SH-233). */
    const val POLICIES = "file_drop_policies"
    const val DOCUMENTS = "file_drop_documents"
    const val SALES = "file_drop_sales"
    const val PROFILE_PHOTO = "file_drop_profile_photo"
    fun coursePage(pageId: String) = "file_drop_course_page_$pageId"
}

/**
 * SH-233: the drop of one element, for UI tests: hands the candidates to the element's handler as a
 * real drop would (no system drag and drop there).
 */
val FileDropAction = SemanticsPropertyKey<(List<DropCandidate>) -> Boolean>("FileDropAction")
private var SemanticsPropertyReceiver.fileDropAction by FileDropAction

/** Whether files can be dragged onto the app here (false on iOS: the pickers are the way in). */
expect val supportsFileDrop: Boolean

/**
 * The platform's drop of files on this element: [onDragging] while files are held over it,
 * [onDropped] with what was dropped (true: the drop was taken). Compose's drag and drop on the
 * desktop and Android/ChromeOS; DOM listeners in the browser, which picks the element under the
 * pointer (or the only one on the screen); nothing on iOS.
 */
@Composable
internal expect fun Modifier.platformFileDrop(
    enabled: Boolean,
    onDragging: (Boolean) -> Unit,
    onDropped: (List<DropCandidate>) -> Boolean
): Modifier

/** The state of one drop place: whether files are held over it and what the person was last told. */
@Stable
class FileDropState internal constructor(internal val handler: FileDropHandler) {
    var dragging by mutableStateOf(false)
        internal set
    var message by mutableStateOf<UiText?>(null)

    /** Handles a drop of [candidates] (what the platform, or a UI test, dropped). */
    fun drop(candidates: List<DropCandidate>): Boolean {
        dragging = false
        return handler.drop(candidates)
    }
}

/** A drop place taking files by [spec], handing them to [onFiles] ([FileDropHandler]). */
@Composable
fun rememberFileDropState(spec: FileDropSpec, onFiles: (List<DroppedFile?>) -> Unit): FileDropState {
    val files = rememberUpdatedState(onFiles)
    val state = remember {
        lateinit var created: FileDropState
        created = FileDropState(FileDropHandler(spec, { files.value(it) }, { created.message = it }))
        created
    }
    state.handler.spec = spec
    return state
}

/** Takes files dropped on this element into [state] while [enabled] (and where files can be dropped). */
@Composable
fun Modifier.fileDropTarget(state: FileDropState, enabled: Boolean = true): Modifier {
    val active = enabled && supportsFileDrop
    return this
        .semantics { if (active) fileDropAction = state::drop }
        .platformFileDrop(active, onDragging = { state.dragging = it }, onDropped = state::drop)
}

/**
 * SH-233: an upload place that also takes a dropped file (web, desktop, Android/ChromeOS): while a
 * file is held over it a highlight says [dropLabel]; a dropped file is checked against [spec] (the
 * same types and size as the place's picker) and handed to [onFiles], the picker's handler. A file
 * that does not fit gets a friendly message: on [snackbarHostState] when given, else below the
 * content. The pickers' buttons stay as they are (phones, tablets, iOS). Several files go in at
 * once where [spec] says [FileDropSpec.multiple]; [FileDropArea] is the one-file form.
 */
@Composable
fun FilesDropArea(
    spec: FileDropSpec,
    dropLabel: String,
    onFiles: (List<DroppedFile?>) -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    snackbarHostState: SnackbarHostState? = null,
    tag: String = FileDropTags.AREA,
    round: Boolean = false,
    content: @Composable () -> Unit
) {
    val state = rememberFileDropState(spec, onFiles)
    val message = state.message?.asString()
    if (snackbarHostState != null) {
        LaunchedEffect(message) {
            // Cleared once shown: clearing first would end this effect before the snackbar shows.
            message?.let {
                snackbarHostState.showSnackbar(it)
                state.message = null
            }
        }
    }
    Column(modifier) {
        Box(Modifier.testTag(tag).fileDropTarget(state, enabled)) {
            content()
            if (state.dragging) FileDropHighlight(dropLabel, Modifier.matchParentSize(), round)
        }
        if (snackbarHostState == null && message != null) {
            FileDropMessage(message, onDismiss = { state.message = null }, modifier = Modifier.padding(top = 8.dp))
        }
    }
}

/** [FilesDropArea] for a place that takes one file: [onFile] gets it (null: it could not be read). */
@Composable
fun FileDropArea(
    spec: FileDropSpec,
    dropLabel: String,
    onFile: (DroppedFile?) -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    snackbarHostState: SnackbarHostState? = null,
    tag: String = FileDropTags.AREA,
    round: Boolean = false,
    content: @Composable () -> Unit
) {
    val file = rememberUpdatedState(onFile)
    FilesDropArea(
        spec = spec.copy(multiple = false),
        dropLabel = dropLabel,
        onFiles = { files -> file.value(files.firstOrNull()) },
        modifier = modifier,
        enabled = enabled,
        snackbarHostState = snackbarHostState,
        tag = tag,
        round = round,
        content = content
    )
}

/**
 * The highlight over a drop place while a file is held over it: a dashed gold frame and [label];
 * [round] (a small round place such as the avatar): a dashed circle with the icon, [label] read out.
 */
@Composable
fun FileDropHighlight(label: String, modifier: Modifier = Modifier, round: Boolean = false) {
    val gold = Gold
    val shape = if (round) CircleShape else RoundedCornerShape(16.dp)
    Box(
        modifier
            .testTag(FileDropTags.HIGHLIGHT)
            .semantics { contentDescription = label }
            .background(GoldTintedWhite.copy(alpha = 0.94f), shape)
            .drawBehind {
                val stroke = Stroke(width = 3.dp.toPx(), pathEffect = PathEffect.dashPathEffect(floatArrayOf(10.dp.toPx(), 6.dp.toPx())))
                if (round) drawCircle(color = gold, style = stroke)
                else drawRoundRect(color = gold, cornerRadius = CornerRadius(16.dp.toPx()), style = stroke)
            }
            .padding(12.dp),
        contentAlignment = Alignment.Center
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Icon(Icons.Default.UploadFile, contentDescription = null, tint = gold, modifier = Modifier.size(32.dp))
            if (!round) Text(label, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, color = DeepNavy, textAlign = TextAlign.Center)
        }
    }
}

/** Why a dropped file was not taken (or which were left out), with a close button. */
@Composable
fun FileDropMessage(text: String, onDismiss: () -> Unit, modifier: Modifier = Modifier) {
    Row(
        modifier
            .fillMaxWidth()
            .testTag(FileDropTags.MESSAGE)
            .background(MaterialTheme.colorScheme.errorContainer, RoundedCornerShape(12.dp))
            .padding(start = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(Icons.Default.ErrorOutline, contentDescription = null, tint = MaterialTheme.colorScheme.onErrorContainer, modifier = Modifier.size(20.dp))
        Text(
            text,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onErrorContainer,
            modifier = Modifier.weight(1f).padding(horizontal = 8.dp, vertical = 10.dp)
        )
        IconButton(onClick = onDismiss) {
            Icon(Icons.Default.Close, contentDescription = stringResource(Res.string.close), tint = MaterialTheme.colorScheme.onErrorContainer)
        }
    }
}
